import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { AuditLoggers } from '../../utils/auditLogger';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Button,
  Select, MenuItem, FormControl, CircularProgress, Alert, Snackbar, TextField,
  Autocomplete, Popper, ClickAwayListener, IconButton, Breadcrumbs, Link, Chip,
  TableHead,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UndoIcon from '@mui/icons-material/Undo';
import { toChartOfAccountGlSystem } from '../../constants/glSyncDocumentTypes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateFormFieldsSx,
  harmonateUploadOutlinedSx,
} from '../../theme/harmonateStyles';

const fetchDefaultCashReconPropertyId = async (tenantId, token) => {
  if (!tenantId || !token) return null;
  try {
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
    const url = new URL(`${baseUrl}/conductor/cash-recon/properties/by-tenant-and-status`);
    url.searchParams.set('tenant_id', tenantId);
    url.searchParams.set('status', 'ACTIVE');
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      console.warn('cash-recon properties by-tenant-and-status failed:', res.status);
      return null;
    }
    const list = await res.json().catch(() => []);
    if (!Array.isArray(list) || list.length === 0) return null;
    const byName = list.find((p) => String(p.propertyName || '').toLowerCase().trim() === 'default');
    const byCode = list.find((p) => String(p.propertyCode || '').toUpperCase().trim() === 'DF');
    const chosen = byName || byCode || list[0];
    return chosen?.propertyId ?? null;
  } catch (e) {
    console.error('fetchDefaultCashReconPropertyId:', e);
    return null;
  }
};

const GLSyncMappingEdit = () => {
  const navigate = useNavigate();
  const [sourceGLAccounts, setSourceGLAccounts] = useState([]);
  const [targetGLAccounts, setTargetGLAccounts] = useState([]);
  const [mappingData, setMappingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('error');
  const [searchValues, setSearchValues] = useState({}); // Track search values for each dropdown
  const { tenantId } = useParams();
  const location = useLocation();

  // Get mappingInstanceId, systems, and fund scope from navigation state (card Edit)
  const {
    mappingInstanceId,
    sourceGL,
    destinationGL,
    fundId: navFundId = null,
    fundCode: navFundCode = null,
    fundName: navFundName = null,
  } = location.state || {};
  const cardFundId = navFundId ? String(navFundId) : null;
  const cardFundCode = navFundCode ? String(navFundCode).trim() : null;
  const cardFundName = navFundName ? String(navFundName).trim() : null;
  const cardFundLabel = cardFundCode || cardFundName || null;

  const [fuzzyMatches, setFuzzyMatches] = useState({});
  // const handleUseSuggestedMatch = (index, suggestedValue) => {
  //   if (!suggestedValue) return;

  //   const updatedData = [...mappingData];
  //   updatedData[index] = {
  //     ...updatedData[index],
  //     destinationField: suggestedValue,
  //     // Update other necessary fields if needed
  //   };
  //   setMappingData(updatedData);
  // };
  const handleUseSuggestedMatch = async (index, suggestedValue) => {
    if (!suggestedValue) return;

    try {
      const updatedData = [...mappingData];
      const sourceAccount = updatedData[index];

      // Find the target account that matches the suggested value
      const targetAccount = targetGLAccounts.find(acc =>
        `${acc.glCode} - ${acc.glDescription}` === suggestedValue
      );

      if (!targetAccount) {
        throw new Error('Target account not found');
      }

      // Save the mapping
      const result = await saveGLLinkMapping(sourceAccount, targetAccount);

      if (result) {
        // Update the UI to reflect the successful mapping (glAccountMappingId from Conductor upsert in saveGLLinkMapping)
        updatedData[index] = {
          ...sourceAccount,
          destinationField: suggestedValue,
          destinationGlCode: targetAccount.glCode,
          mappingId: result.id || sourceAccount.mappingId,
          glAccountMappingId: result.glAccountMappingId ?? sourceAccount.glAccountMappingId ?? null
        };
        setMappingData(updatedData);
        showToast('Mapping saved successfully', 'success');
        
        // Log the mapping update from suggested match
        try {
          await AuditLoggers.glSyncMappingUpdated(
            sourceAccount.glSystem,
            sourceAccount.glCode,
            sourceAccount.glDescription,
            targetAccount.glSystem,
            targetAccount.glCode,
            targetAccount.glDescription,
            sourceAccount.mappingId || null,
            {
              tenantId: (localStorage.getItem('tenant_integration') || tenantId)?.trim(),
              userId: localStorage.getItem('username') || 'unknown',
              action: sourceAccount.mappingId ? 'update' : 'create',
              source: 'suggested_match',
              timestamp: new Date().toISOString()
            }
          );
        } catch (logError) {
          console.error('Failed to log GL Sync mapping update from suggested match:', logError);
          // Don't fail the operation if logging fails
        }
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      showToast(error.message || 'Failed to save mapping', 'error');
    }
  };
  
  const fetchFuzzyMatches = async () => {
    const token = localStorage.getItem('token');
    const storedTenantId = (localStorage.getItem('tenant_integration') || tenantId)?.trim();

    try {
      // Add _GL suffix to source and destination GL values if not already present
      const fromGlSource = sourceGL.endsWith('_GL') ? sourceGL : `${sourceGL}_GL`;
      const toGlSource = destinationGL.endsWith('_GL') ? destinationGL : `${destinationGL}_GL`;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/fuzzy-matched-agg/` +
        `?from_gl_source=${encodeURIComponent(fromGlSource)}` +
        `&to_gl_source=${encodeURIComponent(toGlSource)}` +
        `&tenant=${encodeURIComponent(storedTenantId)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch fuzzy matches');
      }

      const data = await response.json();
      // Convert array to object with from_gl_code as key for easier lookup
      const matchesMap = {};
      data.forEach(item => {
        matchesMap[item.from_gl_code] = item.best_matching_gl_account;
      });
      setFuzzyMatches(matchesMap);
    } catch (error) {
      console.error('Error fetching fuzzy matches:', error);
      // Don't fail the whole component if fuzzy matches fail
    }
  };

  // Function to show toast notification
  const showToast = (message, severity = 'error') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Function to handle toast close
  const handleToastClose = () => {
    setToastOpen(false);
  };

  // Function to handle search input change
  const handleSearchChange = (index, value) => {
    setSearchValues(prev => ({
      ...prev,
      [index]: value
    }));
  };

  // Function to filter destination options based on search
  const getFilteredOptions = (index) => {
    const searchValue = searchValues[index] || '';
    if (!searchValue.trim()) {
      return destinationOptions;
    }

    return destinationOptions.filter(option =>
      option.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const findConductorGlAccountMappingId = (fromCode, toCode, list) => {
    if (!Array.isArray(list)) return null;
    const fc = String(fromCode ?? '').trim();
    const tc = String(toCode ?? '').trim();
    const match = list.find(
      (r) =>
        String(r.fromGlCode ?? '').trim() === fc &&
        String(r.toGlCode ?? '').trim() === tc
    );
    return match?.glAccountMappingId ?? null;
  };

  /** DELETE conductor/gl-account-mappings/{id}; id from load/save Conductor by-filter response */
  const deleteConductorGlAccountMappingById = async (glAccountMappingId, token) => {
    if (!glAccountMappingId) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
    const delRes = await fetch(`${base}conductor/gl-account-mappings/${glAccountMappingId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!delRes.ok && delRes.status !== 404) {
      throw new Error(`Failed to delete conductor GL account mapping (${delRes.status})`);
    }
  };

  /** Prefer stored glAccountMappingId; else GET by-filter and match pair (fallback) */
  const deleteConductorGlAccountMappingForRow = async (mappingRow, token) => {
    if (!sourceGL || !destinationGL || !mappingRow?.glCode) return;
    if (mappingRow.destinationGlCode == null || mappingRow.destinationGlCode === '') return;

    if (mappingRow.glAccountMappingId) {
      await deleteConductorGlAccountMappingById(mappingRow.glAccountMappingId, token);
      return;
    }

    const tenant_id = (localStorage.getItem('tenant_id') || tenantId)?.trim();
    if (!tenant_id) {
      console.warn('No tenant_id for conductor gl-account-mapping delete');
      return;
    }

    const from_gl_source = (sourceGL.endsWith('_GL') ? sourceGL : `${sourceGL}_GL`).toUpperCase();
    const to_gl_source = (destinationGL.endsWith('_GL') ? destinationGL : `${destinationGL}_GL`).toUpperCase();
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
    const listUrl = new URL(`${base}conductor/gl-account-mappings/by-filter`);
    listUrl.searchParams.set('tenant_id', tenant_id);
    listUrl.searchParams.set('from_gl_source', from_gl_source);
    listUrl.searchParams.set('to_gl_source', to_gl_source);
    listUrl.searchParams.set('fund_filter', 'true');
    if (cardFundCode) {
      listUrl.searchParams.set('fund_code', cardFundCode);
    }

    const getRes = await fetch(listUrl.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!getRes.ok) {
      const text = await getRes.text().catch(() => '');
      throw new Error(`Failed to load conductor GL account mappings: ${getRes.status} ${text}`);
    }
    const list = await getRes.json();
    const id = findConductorGlAccountMappingId(mappingRow.glCode, mappingRow.destinationGlCode, list);
    if (!id) {
      console.warn('No matching conductor gl-account-mapping for', mappingRow.glCode, '->', mappingRow.destinationGlCode);
      return;
    }
    await deleteConductorGlAccountMappingById(id, token);
  };

  // Function to handle delete mapping
  const handleDeleteMapping = async (index) => {
    console.log('handleDeleteMapping called with index:', index);
    const mapping = mappingData[index];
    console.log('Mapping to delete:', mapping);

    if (!mapping || (!mapping.mappingId && !mapping.glAccountMappingId)) {
      const errorMsg = 'No valid mapping to delete' + (mapping ? ' (no mapping id)' : ' (mapping not found)');
      console.error(errorMsg, { index, mapping });
      showToast(errorMsg, 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const storedTenantId = (localStorage.getItem('tenant_integration') || tenantId)?.trim();
      console.log('Auth check - token exists:', !!token, 'tenantId:', storedTenantId);

      if (!token) {
        const errorMsg = 'Authentication token not found';
        console.error(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }

      // Legacy link-table row (optional)
      if (mapping.mappingId && storedTenantId) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/${mapping.mappingId}?tenant=${storedTenantId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok && response.status !== 404) {
          console.warn('Failed to delete legacy GL link mapping:', response.status);
        }
      }

      await deleteConductorGlAccountMappingForRow(mapping, token);

      // Log the deletion
      void (async () => {
        try {
          await AuditLoggers.glSyncMappingDeleted(
            mapping.glSystem,
            mapping.glCode,
            mapping.glDescription,
            mapping.mappingId || mapping.glAccountMappingId,
            {
              tenantId: storedTenantId,
              userId: localStorage.getItem('username') || 'unknown',
              timestamp: new Date().toISOString()
            }
          );
        } catch (logError) {
          console.error('Failed to log GL Sync mapping deletion:', logError);
        }
      })();

      const updatedData = [...mappingData];
      updatedData[index] = {
        ...updatedData[index],
        destinationField: '-- No Mapping --',
        destinationGlCode: null,
        mappingId: null,
        glAccountMappingId: null
      };

      setMappingData(updatedData);
      showToast('Mapping deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting mapping:', error);
      showToast('Failed to delete mapping. Please try again.', 'error');
    }
  };

  // Remove stepper state - this is now edit-only

  // Function to fetch GL accounts for the specific mapping instance
  const fetchGLAccountsForMapping = async () => {
    const token = localStorage.getItem('token');
    const storedTenantId = (localStorage.getItem('tenant_id') || tenantId)?.trim();

    if (!token || !storedTenantId) {
      setError('Authentication token or tenant ID not found');
      return { sourceAccounts: [], targetAccounts: [] };
    }

    if (!sourceGL || !destinationGL) {
      setError('Source and destination GL systems are required');
      return { sourceAccounts: [], targetAccounts: [] };
    }

    const fetchSystemAccounts = async (systemName, { fundFilter = false, fundId = null } = {}) => {
      const primaryGlSystem = toChartOfAccountGlSystem(systemName);
      const legacyGlSystem =
        primaryGlSystem.endsWith('_GL') && primaryGlSystem.length > 3
          ? primaryGlSystem.slice(0, -3)
          : '';

      const fetchOnce = async (glSystem) => {
        const url = new URL(
          `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property`
        );
        url.searchParams.set('tenant_id', storedTenantId);
        url.searchParams.set('gl_system', glSystem);
        url.searchParams.set('system_type', 'GL_SYNC');
        if (fundFilter) {
          url.searchParams.set('fund_filter', 'true');
          if (fundId) {
            url.searchParams.set('fund_id', fundId);
          }
        }
        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          let detail = `Failed to fetch GL accounts (${response.status})`;
          try {
            const errJson = await response.json();
            detail = errJson.detail || errJson.message || errJson.error || detail;
          } catch (_) {
            /* keep detail */
          }
          throw new Error(detail);
        }
        const raw = await response.json();
        const list = Array.isArray(raw) ? raw : [];
        return list.map((row) => ({
          ...row,
          glId: row.glId ?? row.ledgerAccountId ?? null,
        }));
      };

      let rows = await fetchOnce(primaryGlSystem);
      if (rows.length === 0 && legacyGlSystem && legacyGlSystem !== primaryGlSystem) {
        rows = await fetchOnce(legacyGlSystem);
      }
      return rows;
    };

    try {
      // Source COA: fund-scoped (same as New Mapping). Destination: all for that system.
      const [sourceAccounts, targetAccounts] = await Promise.all([
        fetchSystemAccounts(sourceGL, { fundFilter: true, fundId: cardFundId }),
        fetchSystemAccounts(destinationGL, { fundFilter: false }),
      ]);
      return { sourceAccounts, targetAccounts };
    } catch (error) {
      console.error('Error fetching GL accounts for mapping:', error);
      setError(error.message);
      return { sourceAccounts: [], targetAccounts: [] };
    }
  };

  // Function to fetch GL Link Mappings
  // const fetchGLLinkMappings = async () => {
  //   const token = localStorage.getItem('token');
  //   const storedTenantId = (localStorage.getItem('tenant_integration') || tenantId)?.trim();

  //   if (!token || !storedTenantId) {
  //     setError('Authentication token or tenant ID not found');
  //     return [];
  //   }

  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/?tenant=${storedTenantId}`, {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch GL Link Mappings');
  //     }

  //     return await response.json();
  //   } catch (error) {
  //     console.error('Error fetching GL Link Mappings:', error);
  //     setError(error.message);
  //     return [];
  //   }
  // };
  const fetchGLLinkMappings = async () => {
    const token = localStorage.getItem('token');
    const storedTenantId = (localStorage.getItem('tenant_integration') || tenantId)?.trim();
  
    if (!token || !storedTenantId) {
      setError('Authentication token or tenant ID not found');
      return [];
    }
  
    try {
      // Add _GL suffix to source and destination GL values if not already present
      const fromGlSource = sourceGL.endsWith('_GL') ? sourceGL : `${sourceGL}_GL`;
      const toGlSource = destinationGL.endsWith('_GL') ? destinationGL : `${destinationGL}_GL`;
  
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/` +
        `?tenant=${encodeURIComponent(storedTenantId)}` +
        `&from_gl_source=${encodeURIComponent(fromGlSource)}` +
        `&to_gl_source=${encodeURIComponent(toGlSource)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch GL Link Mappings');
      }
  
      return await response.json();
    } catch (error) {
      console.error('Error fetching GL Link Mappings:', error);
      setError(error.message);
      return [];
    }
  };

  const fetchConductorGlAccountMappings = async () => {
    const token = localStorage.getItem('token');
    const tenant_id = (localStorage.getItem('tenant_id') || tenantId)?.trim();

    if (!token || !tenant_id || !sourceGL || !destinationGL) {
      return [];
    }

    try {
      const from_gl_source = (sourceGL.endsWith('_GL') ? sourceGL : `${sourceGL}_GL`).toUpperCase();
      const to_gl_source = (destinationGL.endsWith('_GL') ? destinationGL : `${destinationGL}_GL`).toUpperCase();
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
      const listUrl = new URL(`${base}conductor/gl-account-mappings/by-filter`);
      listUrl.searchParams.set('tenant_id', tenant_id);
      listUrl.searchParams.set('from_gl_source', from_gl_source);
      listUrl.searchParams.set('to_gl_source', to_gl_source);
      listUrl.searchParams.set('fund_filter', 'true');
      if (cardFundCode) {
        listUrl.searchParams.set('fund_code', cardFundCode);
      }

      const getRes = await fetch(listUrl.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!getRes.ok) {
        console.warn('Conductor gl-account-mappings by-filter failed:', getRes.status);
        // Fallback: fetch all and filter client-side (older API without fund_filter)
        const fallbackUrl = new URL(`${base}conductor/gl-account-mappings/by-filter`);
        fallbackUrl.searchParams.set('tenant_id', tenant_id);
        fallbackUrl.searchParams.set('from_gl_source', from_gl_source);
        fallbackUrl.searchParams.set('to_gl_source', to_gl_source);
        const fbRes = await fetch(fallbackUrl.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!fbRes.ok) return [];
        const all = await fbRes.json();
        const list = Array.isArray(all) ? all : [];
        return list.filter((row) => {
          const rc = String(row.fundCode ?? row.fund_code ?? '').trim();
          if (cardFundCode) return rc.toLowerCase() === cardFundCode.toLowerCase();
          return !rc;
        });
      }

      const list = await getRes.json();
      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error('Error fetching conductor GL account mappings:', error);
      return [];
    }
  };

  // Stepper removed - this is now edit-only

  // Handle destination field change
  const handleDestinationChange = async (index, value) => {
    try {
      const previousRow = mappingData[index];
      const selectedTarget = targetGLAccounts.find(
        acc => `${acc.glCode} - ${acc.glDescription}` === value
      );

      const updatedData = [...mappingData];
      updatedData[index] = {
        ...updatedData[index],
        destinationField: value,
        destinationGlCode: selectedTarget ? selectedTarget.glCode : null
      };

      setMappingData(updatedData);

      // Save the mapping
      if (selectedTarget) {
        const saveResult = await saveGLLinkMapping(updatedData[index], selectedTarget);

        // If save failed, revert the mapping data to previous state
        if (saveResult === false) {
          const revertedData = [...mappingData];
          setMappingData(revertedData);
          return; // Don't proceed further if save failed
        }

        // Update the mapping data with the returned mapping ID
        if (saveResult && saveResult.id) {
          const finalUpdatedData = [...updatedData];
          finalUpdatedData[index] = {
            ...finalUpdatedData[index],
            mappingId: saveResult.id,
            glAccountMappingId: saveResult.glAccountMappingId ?? finalUpdatedData[index].glAccountMappingId ?? null
          };
          setMappingData(finalUpdatedData);
        }
      } else if (updatedData[index].mappingId) {
        // If no target selected but mapping exists, delete it (pass previous row for conductor delete)
        await deleteGLLinkMapping(updatedData[index].mappingId, previousRow);
      }

      // Show success toast if save was successful
      if (selectedTarget) {
        showToast('Mapping saved successfully!', 'success');
        
        // Log the mapping update
        try {
          await AuditLoggers.glSyncMappingUpdated(
            mappingData[index].glSystem,
            mappingData[index].glCode,
            mappingData[index].glDescription,
            selectedTarget.glSystem,
            selectedTarget.glCode,
            selectedTarget.glDescription,
            mappingData[index].mappingId || null,
            {
              tenantId: (localStorage.getItem('tenant_integration') || tenantId)?.trim(),
              userId: localStorage.getItem('username') || 'unknown',
              action: mappingData[index].mappingId ? 'update' : 'create',
              source: 'dropdown_selection',
              timestamp: new Date().toISOString()
            }
          );
        } catch (logError) {
          console.error('Failed to log GL Sync mapping update:', logError);
          // Don't fail the operation if logging fails
        }
      }
    } catch (error) {
      console.error('Error updating mapping:', error);
      showToast('Failed to update mapping. Please try again.', 'error');
    }
  };

  /** Conductor gl-account-mappings: POST create or PUT when glAccountMappingId exists (camelCase body). */
  const upsertConductorGlAccountMapping = async (sourceAccount, targetAccount, fromGlSourceNorm, toGlSourceNorm, token) => {
    const conductorTenantId = (localStorage.getItem('tenant_id') || tenantId)?.trim();
    if (!conductorTenantId || !token) {
      console.warn('Skipping Conductor gl-account-mapping: missing tenant_id or token');
      return sourceAccount.glAccountMappingId ?? null;
    }

    const fromSrc = String(fromGlSourceNorm).toUpperCase();
    const toSrc = String(toGlSourceNorm).toUpperCase();
    const fromDesc = sourceAccount.glDescription ?? null;
    const toDesc = targetAccount.glDescription ?? null;
    const fromAccountLabel = `${sourceAccount.glCode} - ${fromDesc ?? ''}`;
    const toAccountLabel = `${targetAccount.glCode} - ${toDesc ?? ''}`;
    const descPart = (d) => (d == null ? '' : String(d).trim());

    const rawPropertyId =
      localStorage.getItem('property_id') || localStorage.getItem('selected_property_id') || '';
    let propertyIdForMappings =
      rawPropertyId && String(rawPropertyId).trim() !== '' ? String(rawPropertyId).trim() : null;
    if (!propertyIdForMappings) {
      propertyIdForMappings = await fetchDefaultCashReconPropertyId(conductorTenantId, token);
    }

    const actorId =
      (localStorage.getItem('user_id') || localStorage.getItem('username') || '')?.trim() || null;

    const body = {
      tenantId: conductorTenantId,
      propertyId: propertyIdForMappings ?? null,
      fundId: cardFundId,
      fundCode: cardFundCode,
      fundName: cardFundName,
      fromGlSource: fromSrc,
      fromGlCode: sourceAccount.glCode,
      fromGlDescription: fromDesc,
      fromGlAccount: fromAccountLabel,
      fromCombined: `${fromSrc} | ${String(sourceAccount.glCode).trim()} | ${descPart(fromDesc)}`,
      toGlSource: toSrc,
      toGlCode: targetAccount.glCode,
      toGlDescription: toDesc,
      toGlAccount: toAccountLabel,
      toCombined: `${toSrc} | ${String(targetAccount.glCode).trim()} | ${descPart(toDesc)}`,
      createdBy: actorId,
      updatedBy: actorId,
    };

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
    const existingId = sourceAccount.glAccountMappingId;
    const url = existingId
      ? `${base}conductor/gl-account-mappings/${existingId}`
      : `${base}conductor/gl-account-mappings`;
    const method = existingId ? 'PUT' : 'POST';

    const cRes = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!cRes.ok) {
      const errBody = await cRes.json().catch(() => ({}));
      const msg =
        errBody.message ||
        errBody.detail ||
        errBody.error ||
        `Conductor gl-account-mapping save failed (${cRes.status})`;
      showToast(msg, 'error');
      return sourceAccount.glAccountMappingId ?? null;
    }

    const cJson = await cRes.json().catch(() => ({}));
    return cJson.glAccountMappingId ?? existingId ?? null;
  };

  const resolveSnowflakeTenantId = () => {
    const raw = String(localStorage.getItem('tenant_integration') || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    const dbToLogical = { DEV_T_101: 'T_247' };
    if (dbToLogical[upper]) return dbToLogical[upper];
    if (upper.startsWith('T_')) return upper;
    if (/^\d+$/.test(upper)) return `T_${upper}`;
    return upper;
  };

  /** Push fund-scoped Postgres mappings → Snowflake POSTGRES.GL_ACCOUNT_MAPPING */
  const pushConductorMappingsToSnowflake = async (fromGlSourceNorm, toGlSourceNorm, token) => {
    const conductorTenantId = (localStorage.getItem('tenant_id') || tenantId)?.trim();
    const snowflakeTenantId = resolveSnowflakeTenantId();
    if (!conductorTenantId || !snowflakeTenantId || !token) {
      console.warn('Skipping Snowflake GL_ACCOUNT_MAPPING push: missing tenant/token');
      return;
    }
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
    const pushUrl = new URL(`${base}fastapi/v1/gl-sync/gl-account-mappings/sync-to-snowflake`);
    pushUrl.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
    pushUrl.searchParams.set('tenant_uuid', conductorTenantId);
    if (fromGlSourceNorm) pushUrl.searchParams.set('from_gl_source', String(fromGlSourceNorm).toUpperCase());
    if (toGlSourceNorm) pushUrl.searchParams.set('to_gl_source', String(toGlSourceNorm).toUpperCase());
    pushUrl.searchParams.set('fund_filter', 'true');
    if (cardFundCode) pushUrl.searchParams.set('fund_code', cardFundCode);

    const pushRes = await fetch(pushUrl.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!pushRes.ok) {
      const errBody = await pushRes.json().catch(() => ({}));
      const detail =
        errBody.detail ||
        errBody.message ||
        `Snowflake GL_ACCOUNT_MAPPING push failed (${pushRes.status})`;
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
    return pushRes.json().catch(() => ({}));
  };

  // Save: Postgres gl_account_mapping (fund-scoped) then Snowflake POSTGRES.GL_ACCOUNT_MAPPING
  const saveGLLinkMapping = async (sourceAccount, targetAccount) => {
    const token = localStorage.getItem('token');

    const fromGlSource = sourceAccount.glSystem.endsWith('_GL')
      ? sourceAccount.glSystem
      : `${sourceAccount.glSystem}_GL`;

    const toGlSource = targetAccount.glSystem.endsWith('_GL')
      ? targetAccount.glSystem
      : `${targetAccount.glSystem}_GL`;

    try {
      let glAccountMappingId = sourceAccount.glAccountMappingId ?? null;
      try {
        glAccountMappingId = await upsertConductorGlAccountMapping(
          sourceAccount,
          targetAccount,
          fromGlSource.toUpperCase(),
          toGlSource.toUpperCase(),
          token
        );
      } catch (conductorErr) {
        console.error('Conductor gl-account-mapping upsert error:', conductorErr);
        showToast(
          conductorErr?.message || 'Failed to save Conductor GL account mapping.',
          'error'
        );
        return false;
      }

      if (!glAccountMappingId) {
        showToast('Failed to save Conductor GL account mapping.', 'error');
        return false;
      }

      try {
        await pushConductorMappingsToSnowflake(
          fromGlSource.toUpperCase(),
          toGlSource.toUpperCase(),
          token
        );
      } catch (pushErr) {
        console.error('Snowflake GL_ACCOUNT_MAPPING push error:', pushErr);
        showToast(
          pushErr?.message ||
            'Saved in Conductor but failed to push to Snowflake GL_ACCOUNT_MAPPING.',
          'error'
        );
        // Still return success for Conductor id so UI stays consistent
      }

      return {
        id: sourceAccount.mappingId || null,
        glAccountMappingId,
      };
    } catch (error) {
      console.error('Error saving GL account mapping:', error);
      throw error;
    }
  };

  // Delete GL Link Mapping
  const deleteGLLinkMapping = async (mappingId, rowForConductor = null) => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/${mappingId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to delete mapping');
      }

      if (rowForConductor && token) {
        await deleteConductorGlAccountMappingForRow(rowForConductor, token);
      }

      return true;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!sourceGL || !destinationGL) {
      setError('Source and destination GL systems are required');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [{ sourceAccounts, targetAccounts }] = await Promise.all([
          fetchGLAccountsForMapping(),
          fetchFuzzyMatches() // Fetch fuzzy matches when component mounts
        ]);

        if (!sourceAccounts || sourceAccounts.length === 0) {
          throw new Error('No GL accounts found for the specified systems' + (cardFundLabel ? ` and fund (${cardFundLabel})` : ''));
        }

        // Conductor (fund-scoped) is source of truth; Snowflake links are system-level only
        const [mappings, conductorMappings] = await Promise.all([
          cardFundCode ? Promise.resolve([]) : fetchGLLinkMappings(),
          fetchConductorGlAccountMappings(),
        ]);

        setSourceGLAccounts(sourceAccounts);
        setTargetGLAccounts(targetAccounts);

        const mappingsMap = new Map();
        (mappings || []).forEach((mapping) => {
          if (mapping.from_gl_code) {
            mappingsMap.set(String(mapping.from_gl_code).trim(), mapping);
          }
        });
        const conductorByFromCode = new Map();
        (conductorMappings || []).forEach((row) => {
          const fc = String(row.fromGlCode ?? '').trim();
          if (fc && !conductorByFromCode.has(fc)) {
            conductorByFromCode.set(fc, row);
          }
        });

        // Prefer fund-scoped Conductor mappings; Snowflake only for unscoped cards
        const initialMappingData = sourceAccounts.map((account, index) => {
          const code = String(account.glCode ?? '').trim();
          const conductorRow = conductorByFromCode.get(code);
          const mapping = mappingsMap.get(code);

          let destinationAccount = null;
          if (conductorRow) {
            const toCode = String(conductorRow.toGlCode ?? '').trim();
            destinationAccount = targetAccounts.find((t) => String(t.glCode ?? '').trim() === toCode) || null;
          }
          if (!destinationAccount && mapping) {
            destinationAccount = targetAccounts.find((t) => t.glCode === mapping.to_gl_code) || null;
          }

          const glAccountMappingId = destinationAccount
            ? findConductorGlAccountMappingId(
                account.glCode,
                destinationAccount.glCode,
                conductorMappings
              )
            : conductorRow?.glAccountMappingId ?? null;

          return {
            glId: account.glId,
            glAccount: account.glAccount,
            glCode: account.glCode,
            glDescription: account.glDescription,
            glSystem: account.glSystem,
            destinationField: destinationAccount
              ? `${destinationAccount.glCode} - ${destinationAccount.glDescription}`
              : '-- No Mapping --',
            destinationGlCode: destinationAccount ? destinationAccount.glCode : null,
            highlighted: index % 3 === 1,
            mappingId: mapping ? mapping.id : null,
            glAccountMappingId,
          };
        });

        setMappingData(initialMappingData);
        setError(null);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, sourceGL, destinationGL, cardFundId, cardFundCode]);

  // Create destination options from TARGET accounts
  const destinationOptions = [
    '-- No Mapping --',
    ...targetGLAccounts.map(account => `${account.glCode} - ${account.glDescription}`)
  ];

  if (!mappingInstanceId) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3 }}>
        <Alert severity="error" sx={{ m: 0, maxWidth: 560 }}>
          Mapping instance ID is required to edit mappings.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          ...harmonatePageSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100%',
        }}
      >
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3 }}>
        <Alert severity="error" sx={{ m: 0, maxWidth: 560 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      ...harmonatePageSx,
      ...harmonateFormFieldsSx,
      minHeight: '100vh',
    }}>
      {/* Breadcrumbs with improved spacing */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
          sx={{ fontSize: '0.9rem', mb: 1, '& .MuiBreadcrumbs-ol': { alignItems: 'center' } }}
        >
          <Link
            underline="hover"
            onClick={() => navigate('/gl-sync')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: ht.white,
              '&:hover': { color: ht.white },
            }}
          >
            <HomeIcon sx={{ fontSize: '1rem' }} />
            GL Sync
          </Link>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white, fontWeight: 600 }}>
            GL Mapping Edit
          </Typography>
        </Breadcrumbs>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          GL Mapping Editor
        </Typography>
      </Box>


      {/* Main content card — fixed viewport height + scrollable grid (same idea as GL Sync mapping / DataGrid pages) */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          mb: 4,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)' },
          maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)' },
        }}
      >
        {/* Header with improved styling */}
        <Box sx={{
          p: 2.5,
          backgroundColor: ht.green,
          color: ht.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: '1.1rem',
            color: ht.white,
          }}>
            Mapping
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip
              label={`Source: ${sourceGL}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: ht.white,
                fontWeight: 500
              }}
            />
            <Chip
              label={`Destination: ${destinationGL}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: ht.white,
                fontWeight: 500
              }}
            />
            <Chip
              label={cardFundLabel ? `Fund: ${cardFundLabel}` : 'Fund: Unscoped'}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: ht.white,
                fontWeight: 500
              }}
            />
          </Box>
        </Box>

        {/* Table — scrolls inside fixed card height */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%', minWidth: '150px', backgroundColor: ht.listBg, color: ht.text, fontWeight: 600 }}>Source GL Account</TableCell>
                <TableCell align="center" sx={{ width: '80px', backgroundColor: ht.listBg, color: ht.text, fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ width: '40%', backgroundColor: ht.listBg, color: ht.text, fontWeight: 600 }}>Destination GL Account</TableCell>
                <TableCell sx={{ width: '30%', backgroundColor: ht.listBg, color: ht.text, fontWeight: 600 }}>Suggested Match</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {mappingData.map((row, index) => (
                <TableRow
                  key={row.glId}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: ht.listBg
                    },
                    '&:hover': {
                      backgroundColor: ht.secondaryTab,
                    },
                    '&:last-child td': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <TableCell sx={{
                    width: '20%',  // Reduced from 30%
                    py: 1.75,
                    minWidth: '150px'
                  }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 200 }}>
                        {row.glCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.glDescription}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1.75, width: '80px' }}>  {/* Reduced width */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5
                    }}>
                      {row.mappingId && (
                        <Tooltip title="Delete mapping">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              console.log('Delete button clicked for index:', index);
                              e.stopPropagation(); // Prevent any parent click handlers
                              handleDeleteMapping(index);
                            }}
                            sx={{
                              color: ht.red,
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(239, 63, 63, 0.1)',
                                color: ht.red
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              }
                            }}
                          >
                            <UndoIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <ArrowForwardIcon
                        sx={{
                          fontSize: '1.1rem',  // Smaller arrow
                          color: '#000000'
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell sx={{ py: 1.75 }}>
                    <Autocomplete
                      value={row.destinationField}
                      onChange={(event, newValue) => {
                        handleDestinationChange(index, newValue || '-- No Mapping --');
                      }}
                      options={getFilteredOptions(index)}
                      freeSolo
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search destination..."
                          size="small"
                          variant="outlined"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '13px',
                              borderRadius: ht.fieldRadius,
                              '& fieldset': {
                                borderColor: ht.line,
                              },
                              '&:hover fieldset': {
                                borderColor: ht.text,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: ht.darkBlue,
                                borderWidth: 1,
                              },
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            fontSize: '15px',
                            py: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          {option}
                        </Box>
                      )}
                    />
                  </TableCell>

                  <TableCell sx={{ width: '20%', py: 1.75, px: 2 }}>
                    {fuzzyMatches[row.glCode] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleUseSuggestedMatch(index, fuzzyMatches[row.glCode])}
                          sx={{
                            ...harmonateUploadOutlinedSx,
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderColor: ht.green,
                            color: ht.green,
                            '&:hover': {
                              backgroundColor: 'rgba(2, 107, 29, 0.08)',
                              borderColor: ht.green,
                            },
                          }}
                        >
                          Use
                        </Button>
                        <span style={{ fontSize: '13px' }}>{fuzzyMatches[row.glCode]}</span>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Status bar */}
        <Box sx={{
          p: 1.5,
          backgroundColor: ht.listBg,
          borderTop: `1px solid ${ht.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <Typography variant="body2" sx={{ color: ht.text }}>
            {mappingData.length} accounts
          </Typography>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          )}
        </Box>
      </Paper >

      {/* Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default GLSyncMappingEdit;